import { handleKeyDownWithGridTabbing } from 'Components/Layout/Grid/shared';
import { GridTabDirection } from 'Components/Layout/Grid/types';
import { RootState } from 'Data/Redux';
import { actions } from 'Data/ReduxViewModels/Barcode';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import { matchesTokenisedSearchString } from 'Lib/String/matchesTokenisedSearchString';
import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { WorksheetGridEntryInjectedPropsProvider } from '..';
import { Label, Message } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { autoFocusFunctionArgs } from '../types';
import './WorksheetGrid.scss';

interface IWorksheetGridProps {
  colDefinitions: any;
  tabDirection: GridTabDirection;
}

/**
 * Returns style properties to apply to cells in a given column based on the column definitions
 */
const getCellStyleForColumn = (colDefinitions: any, colIndex: number) => {
  return colDefinitions[colIndex].sticky
    ? {
        ...colDefinitions[colIndex].cellStyle,
        width: colDefinitions[colIndex].width,
        left: colDefinitions.slice(0, colIndex).reduce((a: any, b: any) => a + b.width, 0),
      }
    : { ...colDefinitions[colIndex].cellStyle, width: colDefinitions[colIndex].width };
};

// Creates a grid where each row is a separate worksheet, and each column is a field for that worksheet
const WorksheetGridBase: React.FC<IWorksheetGridProps> = ({
  // [{
  //   field,  // Field component or function receiving worksheet that returns field component
  //   header, // String header for column
  //   width,  // Numeric width of column (in px). Only fixed width columns are currently supported.
  //   hidden, // Boolean. Hides column.
  //   sticky,  // Boolean. If true column will stick in its position when the user scrolls the grid. Note if true all previous columns must be sticky.
  //   cellStyle   // Style object to be applied to each cell in that column
  // }]
  colDefinitions,
  /**
   * 'horizontal' | 'vertical'
   *  See IGridProps.GridTabDirection
   */
  tabDirection,
}) => {
  const worksheetIds = useSelector(
    combine(
      [
        (state: RootState) => Object.values(state.worksheetGrid.worksheets),
        pipe(
          (state: RootState) => state.worksheetGrid.additionalData['SampleIdFilter'],
          (filter: string) => filter?.toLowerCase()
        ),
      ],
      (worksheets, sampleIdFilter) => {
        if (!hasValue(sampleIdFilter)) return worksheets?.map((w: any) => w?.syncModel?.data?.Id);

        return worksheets
          ?.filter((worksheet) =>
            matchesTokenisedSearchString(worksheet.syncModel.header.Sample.SampleId, sampleIdFilter)
          )
          ?.map((w: any) => w?.syncModel?.data?.Id);
      }
    ),
    shallowEqual
  );

  const worksheetLimit = useSelector((state) => state.worksheetGrid.query.params.Limit);

  // Create a focus ref for each cell so we can manipulate tab order
  const focusRefs = worksheetIds?.map((_: Guid) => colDefinitions?.map((_: any) => React.createRef()));

  if (colDefinitions.some((c: any, i: number) => c.sticky && colDefinitions.slice(0, i).some((d: any) => !d.sticky)))
    throw new Error('Columns in a WorksheetGrid component can only be sticky if all previous columns are sticky.');
  if (colDefinitions.some((c: any) => isNaN(c.width)))
    throw new Error('All columns in a WorksheetGrid component must have a fixed numeric width');

  return (
    <div className="WorksheetBulkEntryGrid">
      <div className="WorksheetBulkEntryGrid-Rows">
        <div className="WorksheetBulkEntryGrid-Row WorksheetBulkEntryGrid-Row-header">
          {colDefinitions.map((c: any, i: number) =>
            c.hidden ? null : (
              <Label
                className={classNames('WorksheetBulkEntryGrid-Cell', {
                  'WorksheetBulkEntryGrid-Cell-sticky': c.sticky,
                })}
                style={getCellStyleForColumn(colDefinitions, i)}
                emph
                key={i}
              >
                {c.header}
              </Label>
            )
          )}
          <div
            className="WorksheetBulkEntryGrid-Cell WorksheetBulkEntryGrid-Cell-filler"
            style={{ ...colDefinitions[colDefinitions.length - 1].cellStyle }}
          />
        </div>

        {worksheetIds.map((w: Guid, i: number) => (
          <WorksheetGridRow
            worksheetId={w}
            colDefinitions={colDefinitions}
            focusRefs={focusRefs}
            rowIndex={i}
            tabDirection={tabDirection}
            key={i}
          />
        ))}
        {worksheetIds.length === worksheetLimit && (
          <div className="WorksheetBulkEntryGrid-Row">
            <div className="WorksheetBulkEntryGrid-Cell WorksheetBulkEntryGrid-Cell-message">
              <Message>
                Maximum number of worksheets loaded. Once data entry is complete, refresh to load more worksheets.
              </Message>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface IWorksheetGridRowProps {
  worksheetId: Guid;
  colDefinitions: any;
  focusRefs: any;
  rowIndex: number;
  tabDirection: GridTabDirection;
}

// Each row is a separate component so when we change a single worksheet only that row is re-rendered.
const WorksheetGridRow: React.FC<IWorksheetGridRowProps> = ({
  worksheetId,
  colDefinitions,
  focusRefs,
  rowIndex,
  tabDirection,
}) => {
  const dispatch = useDispatch();

  const setWorksheetAsLastFocused = useCallback(() => {
    dispatch(actions.setFocusedUUID(worksheetId));
  }, [dispatch, worksheetId]);

  return (
    <WorksheetGridEntryInjectedPropsProvider worksheetId={worksheetId}>
      <div className="WorksheetBulkEntryGrid-Row" onFocus={setWorksheetAsLastFocused}>
        {colDefinitions.map((c: any, j: number) =>
          c.hidden ? null : (
            <div
              className={classNames('WorksheetBulkEntryGrid-Cell', { 'WorksheetBulkEntryGrid-Cell-sticky': c.sticky })}
              style={getCellStyleForColumn(colDefinitions, j)}
              key={`${j}`}
            >
              <WorksheetGridCell
                worksheetId={worksheetId}
                cellDefinition={c.field}
                rowIndex={rowIndex}
                colIndex={j}
                focusRefs={focusRefs}
                tabDirection={tabDirection}
                autoFocus={c.autoFocus}
              />
            </div>
          )
        )}
        <div
          className="WorksheetBulkEntryGrid-Cell WorksheetBulkEntryGrid-Cell-filler"
          style={{ ...colDefinitions[colDefinitions.length - 1].cellStyle }}
        />
      </div>
    </WorksheetGridEntryInjectedPropsProvider>
  );
};

interface IWorksheetGridCellProps {
  worksheetId: Guid;
  cellDefinition: JSX.Element | ((worksheet: any, worksheetPath: string) => JSX.Element);
  rowIndex: number;
  colIndex: number;
  focusRefs: any;
  tabDirection: GridTabDirection;
  autoFocus?: autoFocusFunctionArgs;
}

const WorksheetGridCell: React.FC<IWorksheetGridCellProps> = ({
  worksheetId,
  autoFocus,
  cellDefinition,
  tabDirection,
  rowIndex,
  colIndex,
  focusRefs,
}) => {
  const baseElement = useSelector(
    pipe(
      (state) => state.worksheetGrid.worksheets[worksheetId],
      (w) =>
        typeof cellDefinition === 'function'
          ? cellDefinition(w, `worksheetGrid.worksheets[${worksheetId}]`)
          : cellDefinition
    )
  );

  useEffect(() => {
    // In order to focus on a row we need to call the focus function on the respective focusRef once the row has been rendered
    var focusRef = focusRefs?.[rowIndex]?.[colIndex];
    if (hasNonEmptyValue(autoFocus) && autoFocus!(worksheetId, focusRefs, rowIndex, colIndex))
      focusRef?.current && focusRef?.current.focus();
  }, [autoFocus, worksheetId, rowIndex, colIndex, focusRefs]);

  return React.cloneElement(baseElement, {
    inGrid: true,
    truncate: true,
    onKeyDown: handleKeyDownWithGridTabbing(
      baseElement.props.onKeyDown,
      tabDirection === 'vertical',
      focusRefs,
      focusRefs?.[rowIndex]?.[colIndex]
    ),
    focusRef: focusRefs?.[rowIndex]?.[colIndex],
  });
};

const WorksheetGrid = withInjectedProps(WorksheetGridBase);

export default WorksheetGrid;
